import { io, Socket } from 'socket.io-client';

/**
 * Socket.io Service
 * Manages WebSocket connection for real-time updates
 */
class SocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second

  /**
   * Connect to Socket.io server
   */
  connect(): void {
    if (this.socket?.connected) {
      console.log('[Socket] Already connected');
      return;
    }

    const baseURL = process.env.REACT_APP_BASE_URL || 'http://localhost:4001';

    this.socket = io(baseURL, {
      withCredentials: true, // Send cookies
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
      reconnectionDelayMax: 10000,
      timeout: 20000,
    });

    this.setupEventHandlers();
  }

  /**
   * Setup Socket.io event handlers
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('[Socket] Connected successfully');
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[Socket] Disconnected:', reason);

      if (reason === 'io server disconnect') {
        // Server disconnected the socket, try to reconnect manually
        this.reconnect();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('[Socket] Connection error:', error.message);
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('[Socket] Max reconnection attempts reached');
        this.disconnect();
      } else {
        // Exponential backoff
        this.reconnectDelay = Math.min(this.reconnectDelay * 2, 10000);
      }
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('[Socket] Reconnected after', attemptNumber, 'attempts');
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('[Socket] Reconnection attempt', attemptNumber);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('[Socket] Reconnection failed');
      this.disconnect();
    });
  }

  /**
   * Manually reconnect to Socket.io server
   */
  private reconnect(): void {
    if (this.socket && !this.socket.connected) {
      console.log('[Socket] Attempting manual reconnection...');
      this.socket.connect();
    }
  }

  /**
   * Disconnect from Socket.io server
   */
  disconnect(): void {
    if (this.socket) {
      console.log('[Socket] Disconnecting...');
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
    }
  }

  /**
   * Listen to a Socket.io event
   * @param event - Event name
   * @param callback - Event callback
   */
  on<T = any>(event: string, callback: (data: T) => void): void {
    if (!this.socket) {
      console.warn('[Socket] Cannot listen to event, not connected');
      return;
    }

    this.socket.on(event, callback);
  }

  /**
   * Remove a Socket.io event listener
   * @param event - Event name
   * @param callback - Event callback (optional)
   */
  off(event: string, callback?: (data: any) => void): void {
    if (!this.socket) return;

    if (callback) {
      this.socket.off(event, callback);
    } else {
      this.socket.off(event);
    }
  }

  /**
   * Emit a Socket.io event
   * @param event - Event name
   * @param data - Event data
   */
  emit<T = any>(event: string, data?: T): void {
    if (!this.socket || !this.socket.connected) {
      console.warn('[Socket] Cannot emit event, not connected');
      return;
    }

    this.socket.emit(event, data);
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Get socket instance (for advanced usage)
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Join a specific room
   * @param room - Room name
   */
  joinRoom(room: string): void {
    this.emit('join', { room });
  }

  /**
   * Leave a specific room
   * @param room - Room name
   */
  leaveRoom(room: string): void {
    this.emit('leave', { room });
  }
}

export default new SocketService();
